import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  VStack,
  IconButton,
  InputGroup,
  FormErrorMessage,
  Button,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import apiClient from '../lib/api/apiClient';
import { authEndpoints } from '../lib/api/authApiClient';
import { useNavigate } from 'react-router-dom';

interface UsernamePasswordSignInProps {
  onSuccess?: () => void;
}

const UsernamePasswordSignIn = ({ onSuccess }: UsernamePasswordSignInProps) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Email validation regex
  const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
  const isFormValid = isValidEmail(formData.username) && formData.password.length >= 8;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'username' ? value.toLowerCase() : value
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(authEndpoints.login(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // credentials: 'include',
        // mode: 'cors',
        body: JSON.stringify({ username: formData.username, password: formData.password, email: formData.username })
      });
      const data = await response.json();
      if (!data.token || data.error) {
        setError('Sign in failed. Please check your credentials.');
        return;
      }
      navigate(`/dashboard?token=${data.token}&userId=${data.user_id}&email=${formData.username}`);
    } catch (error) {
      console.error('Sign in error:', error);
      setError('Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box w="full">
      <form onSubmit={handleSubmit}>
        <VStack gap={4}>
          <FormControl isInvalid={!!error} isRequired>
            <FormLabel color="white">Email</FormLabel>
            <Input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              bg="transparent"
              color="white"
              borderColor="whiteAlpha.400"
              borderWidth="1.5px"
              _hover={{ borderColor: 'whiteAlpha.600', bg: 'transparent' }}
              _focus={{ borderColor: 'white', bg: 'transparent' }}
              disabled={isLoading}
            />
          </FormControl>

          <FormControl isInvalid={!!error} isRequired>
            <FormLabel color="white">Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                bg="transparent"
                color="white"
                borderColor="whiteAlpha.400"
                borderWidth="1.5px"
                _hover={{ borderColor: 'whiteAlpha.600', bg: 'transparent' }}
                _focus={{ borderColor: 'white', bg: 'transparent' }}
                disabled={isLoading}
              />
              <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  size="sm"
                  variant="ghost"
                  bg="transparent"
                  backgroundColor="transparent"
                  color="white"
                  _hover={{ bg: 'transparent', backgroundColor: 'transparent', color: 'gray.200' }}
                  _active={{ bg: 'transparent', backgroundColor: 'transparent' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={() => setShowPassword((show) => !show)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              </Box>
            </InputGroup>
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>

          <Box w="full">
            <Button
              type="submit"
              w="full"
              variant="dominus"
              isLoading={isLoading}
              isDisabled={!isFormValid || isLoading}
            >
              Sign In
            </Button>
          </Box>
        </VStack>
      </form>
    </Box>
  );
};

export default UsernamePasswordSignIn;